import React, { FC } from "react";
import { Button, Form, FormProps, Input, InputNumber, Radio, Rate, Row, Space, Switch, Typography } from "antd";
import { FrownFilled, MehFilled, SmileFilled } from "@ant-design/icons";

import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { IKnownDictionary } from "@Components/shared/types";

import "@Styles/components/designer.scss";

interface IProps extends Omit<FormProps, "name" | "fields">, IFeedbackTemplateDetails {
    isDisabled?: boolean;
    isLoading?: boolean;
    isFetching?: boolean;

    onSubmit?: (values: IKnownDictionary) => void;
}

const FeedbackFormField: FC<IFeedbackTemplateDetails["fields"][number]> = ({ id, name, options, type }) => {
    const fieldType = () => {
        switch (type) {
            case "text":
                return <Input allowClear={true} />;

            case "textarea":
                return <Input.TextArea allowClear={true} />;

            case "numeric":
                return (
                    <InputNumber
                        controls={true}
                        min={options[0] ?? 0} // Min
                        max={options[1] ?? 100} // Max
                        addonAfter={options[2] === "true" ? "%" : ""} // Display percentage
                    />
                );

            case "boolean":
                return <Switch checkedChildren={"Yes"} unCheckedChildren={"No"} />;

            case "option":
                return (
                    <Radio.Group
                        name={name}
                        defaultValue={options[0] ?? null} // Each option is an option
                    >
                        <Space direction="vertical">
                            {options.map((option) => (
                                <Radio value={option}>{option}</Radio>
                            ))}
                            ;
                        </Space>
                    </Radio.Group>
                );

            case "likert":
                switch (options[5]) {
                    default: // Options - radio buttons
                        return (
                            <Radio.Group>
                                <Space direction="vertical">
                                    {options.slice(0, 5).map((x, i) => {
                                        return (
                                            <Radio style={{ lineHeight: "12px" }} key={i} value={i}>
                                                {x}
                                            </Radio>
                                        );
                                    })}
                                </Space>
                            </Radio.Group>
                        );

                    case "2": // Rating
                        const ratings = options.slice(0, 5);
                        return <Rate tooltips={ratings} />;

                    case "3": // Smileys
                        const smileys = options.slice(0, 5);
                        const customIcons: Record<number, React.ReactNode> = {
                            1: <FrownFilled className={"smiley1"} />,
                            2: <FrownFilled className={"smiley2"} />,
                            3: <MehFilled className={"smiley3"} />,
                            4: <SmileFilled className={"smiley4"} />,
                            5: <SmileFilled className={"smiley5"} />,
                        };
                        return (
                            <Rate
                                tooltips={smileys}
                                character={({ index }: { index: number }) => customIcons[index + 1]}
                            />
                        );
                }

            default:
                return null;
        }
    };

    const initialValuesMap = {
        text: "",
        textarea: "",
        boolean: false,
    };

    return (
        <Form.Item
            name={id}
            label={name}
            rules={[{ required: type === "likert" ? true : false, message: "This field is required" }]}
            initialValue={initialValuesMap[type]}
        >
            {fieldType()}
        </Form.Item>
    );
};

const FeedbackForm: FC<IProps> = ({
    description,
    fields = [],
    groupId,
    isDisabled = false,
    isLoading,
    locked,
    name,
    templateId,

    onSubmit,
    ...otherProps
}) => (
    <section>
        <Row justify="center">
            <Typography.Title level={3}>{name}</Typography.Title>
        </Row>
        <Row justify="center">
            <Typography.Title level={5}>{description}</Typography.Title>
        </Row>
        <Form
            className="designer-item-list"
            disabled={isDisabled || isLoading}
            labelCol={{ xs: 8, sm: 8, md: 10 }}
            labelWrap
            onFinish={onSubmit}
            {...otherProps}
        >
            {fields?.map((field, index) => (
                <FeedbackFormField key={field.id ?? index} {...field} />
            ))}
            {!!onSubmit ? (
                <Row justify="end">
                    <Button className="secondary" htmlType="submit" loading={isLoading}>
                        Complete
                    </Button>
                </Row>
            ) : null}
        </Form>
    </section>
);

export default FeedbackForm;
